export type LoungeMaintenanceConfig = {
  enabled: boolean;
  title: string;
  message: string;
  eta?: string;
  updatedAt?: string;
};

export type LoungeMaintenanceState = {
  config: LoungeMaintenanceConfig;
  isLoading: boolean;
};

export const LOUNGE_MAINTENANCE_STORAGE_KEY = 'mw_lounge_maintenance_preview';
export const LOUNGE_MAINTENANCE_POLL_MS = 60 * 1000;

export const DEFAULT_LOUNGE_MAINTENANCE: LoungeMaintenanceConfig = {
  enabled: false,
  title: 'The Lounge is paused for maintenance',
  message:
    'We are moving MouseWait to new servers and temporarily freezing new posts, comments, likes, and edits so the data can sync cleanly.',
};

const VERSION_URL = '/version.txt';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function cleanString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function parseEnabled(value: unknown): boolean {
  return value === true || value === 'true' || value === '1' || value === 1;
}

export function normalizeLoungeMaintenanceConfig(
  value: unknown
): LoungeMaintenanceConfig {
  if (!isRecord(value)) {
    return { ...DEFAULT_LOUNGE_MAINTENANCE };
  }

  return {
    enabled: parseEnabled(value.enabled),
    title: cleanString(value.title) || DEFAULT_LOUNGE_MAINTENANCE.title,
    message: cleanString(value.message) || DEFAULT_LOUNGE_MAINTENANCE.message,
    eta: cleanString(value.eta),
    updatedAt: cleanString(value.updatedAt),
  };
}

export function getLoungeMaintenanceConfigFromPayload(
  payload: unknown
): LoungeMaintenanceConfig {
  if (!isRecord(payload)) {
    return { ...DEFAULT_LOUNGE_MAINTENANCE };
  }

  return normalizeLoungeMaintenanceConfig(payload.loungeMaintenance);
}

export function readLoungeMaintenancePreviewOverride():
  | LoungeMaintenanceConfig
  | null {
  try {
    const raw = window.localStorage.getItem(LOUNGE_MAINTENANCE_STORAGE_KEY);
    if (!raw) return null;

    if (raw === 'on' || raw === 'true' || raw === '1') {
      return { ...DEFAULT_LOUNGE_MAINTENANCE, enabled: true };
    }

    if (raw === 'off' || raw === 'false' || raw === '0') {
      return { ...DEFAULT_LOUNGE_MAINTENANCE, enabled: false };
    }

    const parsed = JSON.parse(raw);
    return normalizeLoungeMaintenanceConfig(parsed);
  } catch {
    return null;
  }
}

export async function fetchLoungeMaintenanceConfig(
  fetchImpl: typeof fetch = fetch
): Promise<LoungeMaintenanceConfig> {
  try {
    const res = await fetchImpl(VERSION_URL, {
      cache: 'no-store',
      credentials: 'omit',
    });
    if (!res.ok) {
      return { ...DEFAULT_LOUNGE_MAINTENANCE };
    }
    const payload = JSON.parse(await res.text());
    return getLoungeMaintenanceConfigFromPayload(payload);
  } catch {
    return { ...DEFAULT_LOUNGE_MAINTENANCE };
  }
}

export function getEffectiveLoungeMaintenanceConfig(
  remoteConfig: LoungeMaintenanceConfig
): LoungeMaintenanceConfig {
  return readLoungeMaintenancePreviewOverride() || remoteConfig;
}

export function isLoungeMaintenancePath(pathname: string): boolean {
  const path = (pathname || '').split('?')[0].replace(/\/+$/, '') || '/';

  if (path === '/disneyland/lounge') return true;
  if (path === '/disneyworld/lounge') return true;
  if (path === '/disneyworld/the-hub') return true;
  if (path === '/loungeland') return true;
  if (path === '/club333') return true;

  return [
    /^\/disneyland\/search\/post\/[^/]+$/,
    /^\/disneyworld\/search\/post\/[^/]+$/,
    /^\/disneyland\/lands-talk\/[^/]+\/[^/]+$/,
    /^\/disneyworld\/lands-talk\/[^/]+\/[^/]+$/,
    /^\/disneyland\/user\/[^/]+\/mypost$/,
    /^\/disneyworld\/user\/[^/]+\/mypost$/,
    /^\/disneyland\/lands\/[^/]+\/[^/]+$/,
    /^\/disneyworld\/lands\/[^/]+\/[^/]+$/,
    /^\/disneyland\/tag\/[^/]+$/,
    /^\/disneyworld\/tag\/[^/]+$/,
    /^\/disneyland\/hash\/[^/]+$/,
    /^\/disneyworld\/hash\/[^/]+$/,
    /^\/disneyland\/[^/]+\/L\/most-viewed$/,
    /^\/disneyworld\/[^/]+\/L\/most-viewed$/,
    /^\/disneyland\/topmwbyqualitypost$/,
    /^\/disneyworld\/topmwbyqualitypost$/,
    /^\/disneyland\/topmousewaiters\/[^/]+$/,
    /^\/disneyworld\/topmousewaiters\/[^/]+$/,
    /^\/disneyland\/topnewsfeatured$/,
  ].some((pattern) => pattern.test(path));
}
